import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { zql } from '../schema.js';
import { itemsDetail, statusItems } from '../util/event-log-items.js';
import { titleCase } from '../util/index.js';
import { assertCan } from '../util/permissions.js';
import { Address, Status } from '../util/schemas.js';
import { addEventLog } from './util.js';
import { createAddress } from './utils/address-utils.js';
export const SectionSchema = z.object({
    code: z.string(),
    costCenterNumber: z.string(),
    name: z.string(),
    type: z.enum(['pharmacy', 'outpatients']),
    status: Status,
    orderNumber: z.string(),
    movementType: z.string(),
    address: Address,
});
export const AddSectionSchema = SectionSchema;
export const UpdateSectionSchema = AddSectionSchema.partial().extend({ id: z.uuidv7() });
export const UpdateSectionBatchStatusSchema = z.object({ sectionIds: z.array(z.uuidv7()), status: Status });
export const DeleteSectionBatchSchema = z.object({ sectionIds: z.array(z.uuidv7()) });
export const sections = {
    add: defineMutator(AddSectionSchema, async ({ args: section, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        if (section.code) {
            const existingSection = await tx.run(zql.sections.where('code', '=', section.code).one());
            if (existingSection)
                throw new Error('Section code already exists');
        }
        if (section.costCenterNumber) {
            const existingSection = await tx.run(zql.sections.where('costCenterNumber', '=', section.costCenterNumber).one());
            if (existingSection)
                throw new Error('Section cost center already exists');
        }
        const location = await tx.run(zql.locations.one());
        if (!location)
            throw new Error('Could not find location');
        const locationId = location.id;
        const address = await createAddress(tx, section.address, ctx.user.id);
        const newSection = {
            id: uuidv7(),
            name: titleCase(section.name),
            code: section.code,
            locationId: locationId,
            type: section.type ?? 'outpatients',
            status: section.status,
            costCenterNumber: section.costCenterNumber,
            orderNumber: section.orderNumber,
            movementType: section.movementType,
            addressId: address.id,
            createdAt: Date.now(),
            createdBy: ctx.user.id,
        };
        await tx.mutate.sections.insert(newSection);
        if (tx.location === 'server') {
            const patches = createPatch({}, newSection);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'section',
                resourceId: newSection.id,
                action: 'create',
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: Date.now(),
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: newSection.id,
                    name: newSection.name,
                },
            }, ctx);
        }
    }),
    update: defineMutator(UpdateSectionSchema, async ({ tx, ctx, args: section }) => {
        assertCan(ctx, 'organisation.manage');
        if (!section.id)
            throw new Error('Bad Request');
        const prev = await tx.run(zql.sections.where('id', '=', section.id).one());
        if (!prev)
            throw new Error('Not Found');
        if (section.code) {
            const existingSection = await tx.run(zql.sections.where('code', '=', section.code).where('id', '!=', section.id).one());
            if (existingSection)
                throw new Error('Section code already exists');
        }
        if (section.costCenterNumber) {
            const existingSection = await tx.run(zql.sections.where('costCenterNumber', '=', section.costCenterNumber).where('id', '!=', section.id).one());
            if (existingSection)
                throw new Error('Section cost center already exists');
        }
        const now = Date.now();
        let addressId = prev.addressId;
        if (section.address) {
            if (prev.addressId) {
                const updatedAddress = await createAddress(tx, section.address, ctx.user.id);
                addressId = updatedAddress.id;
            }
            else {
                const newAddress = await createAddress(tx, section.address, ctx.user.id);
                addressId = newAddress.id;
            }
        }
        delete section.address;
        await tx.mutate.sections.update({
            ...section,
            id: prev.id,
            name: titleCase(section.name),
            addressId,
            updatedAt: now,
            updatedBy: ctx.user.id,
        });
        if (tx.location === 'server') {
            const patches = createPatch(prev, { ...prev, ...section, updatedAt: now, updatedBy: ctx.user.id });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'section',
                resourceId: prev.id,
                action: 'update',
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: section.id,
                    name: section.name,
                },
            }, ctx);
        }
    }),
    updateStatus: defineMutator(UpdateSectionBatchStatusSchema, async ({ tx, ctx, args }) => {
        assertCan(ctx, 'organisation.manage');
        const sections = await tx.run(zql.sections.related('wards').where('id', 'IN', args.sectionIds));
        for (const section of sections) {
            if (args.status === 'inactive') {
                const activeWards = section.wards.filter(ward => ward.status === 'active');
                if (activeWards.length > 0) {
                    throw new Error(`Section "${section.name}" cannot be deactivated: ${activeWards.length} ward(s) still active`);
                }
            }
        }
        const now = Date.now();
        await Promise.all(sections.map(async (section) => {
            await tx.mutate.sections.update({
                id: section.id,
                status: args.status,
                updatedAt: now,
                updatedBy: ctx.user.id,
            });
        }));
        if (tx.location === 'server') {
            await Promise.all(sections.map(async (section) => {
                const patches = createPatch(section, {
                    ...section,
                    status: args.status,
                    updatedAt: now,
                    updatedBy: ctx.user.id,
                });
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: 'section',
                    resourceId: section.id,
                    action: 'update',
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    status: args.status === 'inactive' ? 'WARNING' : 'INFO',
                    domain: 'configuration',
                    details: {
                        id: section.id,
                        name: section.name,
                        ...itemsDetail(statusItems(section.status, args.status)),
                    },
                }, ctx);
            }));
        }
    }),
    deleteBatch: defineMutator(DeleteSectionBatchSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        const sections = await tx.run(zql.sections.related('wards').where('id', 'IN', args.sectionIds));
        for (const section of sections) {
            const activeWards = section.wards.filter(ward => !ward.deletedAt);
            if (activeWards.length > 0) {
                throw new Error(`Section "${section.name}" cannot be deleted: ${activeWards.length} ward(s) still active`);
            }
        }
        const now = Date.now();
        await Promise.all(sections.map(async (section) => {
            await tx.mutate.sections.update({
                id: section.id,
                deletedAt: now,
                deletedBy: ctx.user.id,
                // update code and costCenterNumber so we can use it for another ward
                code: `${section.code}_deleted_${now}`,
                costCenterNumber: `${section.costCenterNumber}_deleted_${now}`,
            });
        }));
        if (tx.location === 'server') {
            await Promise.all(sections.map(async (section) => {
                const patches = createPatch(section, { ...section, deletedAt: now, deletedBy: ctx.user.id });
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: 'section',
                    resourceId: section.id,
                    action: 'delete',
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    status: 'WARNING',
                    domain: 'configuration',
                    details: {
                        id: section.id,
                        name: section.name,
                    },
                }, ctx);
            }));
        }
    }),
};
//# sourceMappingURL=section.mutators.js.map